import {
  Avatar,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  Rating,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconEdit, IconTag } from "@tabler/icons-react";
import { useAtomValue } from "jotai";
import { User } from "../../../../gql/__generated__/graphql";
import { isTiktokLinkedAtom } from "../../../atoms";
import TikTokLoginButton from "../../../components/TikTokLoginButton/TikTokLoginButton";
import SocialMediaStats from "./SocialMediaStats";

interface ProfileHeaderProps {
  profile: User;
  isMyProfile: boolean;
  averageRating: number;
  numReviews: number;
  onEditClick: () => void;
  showTikTokButton?: boolean;
}

const ProfileHeader = ({
  profile,
  isMyProfile,
  averageRating,
  numReviews,
  onEditClick,
  showTikTokButton = false,
}: ProfileHeaderProps) => {
  const isTiktokLinked = useAtomValue(isTiktokLinkedAtom);

  return (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Stack gap="md">
        <Group wrap="wrap" align="flex-start" justify="center">
          <Box pos="relative">
            <Avatar
              src={profile.imageUrl}
              size={140}
              radius={profile.userType === "BRAND_USER" ? "md" : "xl"}
              style={{
                border:
                  profile.userType === "BRAND_USER"
                    ? "3px solid var(--mantine-color-primary-6)"
                    : "3px solid var(--mantine-color-primary-6)",
              }}
            />
            {/* Role Badge - Prominent positioning */}
            <Badge
              color={profile.userType === "BRAND_USER" ? "primary" : "primary"}
              size="xl"
              variant="filled"
              pos="absolute"
              bottom={-10}
              left="50%"
              style={{
                transform: "translateX(-50%)",
                fontWeight: 700,
                fontSize: "14px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                minWidth: "80px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {profile.userType === "BRAND_USER" ? "Brand" : "Creator"}
            </Badge>
          </Box>

          <Box style={{ flex: 1, minWidth: 0 }}>
            <Stack gap="sm">
              <Box>
                <Title
                  order={1}
                  style={(theme) => ({
                    background:
                      profile.userType === "BRAND_USER"
                        ? `linear-gradient(45deg, ${theme.colors.primary[6]}, ${theme.colors.tertiary[6]})`
                        : `linear-gradient(45deg, ${theme.colors.primary[6]}, ${theme.colors.secondary[6]})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  })}
                >
                  {profile.name}
                </Title>
                <Group gap="xs" mt="xs">
                  {profile.categories?.map((category, index) => (
                    <Badge
                      key={index}
                      color={
                        profile.userType === "BRAND_USER"
                          ? "tertiary"
                          : "secondary"
                      }
                      size="md"
                      variant="light"
                      leftSection={<IconTag size={14} />}
                    >
                      {category}
                    </Badge>
                  ))}
                </Group>
              </Box>
              {isMyProfile && (
                <Group gap="sm" wrap="wrap">
                  {showTikTokButton && !isTiktokLinked && <TikTokLoginButton />}
                  <Button
                    leftSection={<IconEdit size={16} />}
                    color={
                      profile.userType === "BRAND_USER" ? "primary" : "primary"
                    }
                    onClick={onEditClick}
                  >
                    Edit Profile
                  </Button>
                </Group>
              )}
            </Stack>
          </Box>
        </Group>

        <Group align="center" wrap="wrap">
          <Rating
            value={averageRating}
            readOnly
            fractions={2}
            color={profile.userType === "BRAND_USER" ? "primary" : "primary"}
          />
          <Text size="sm">({numReviews} reviews)</Text>
        </Group>

        {profile.userType === "CREATOR" &&
          (profile.socialMedia || profile.tiktokData) && (
            <SocialMediaStats
              socialMedia={profile.socialMedia ?? null}
              tiktokData={profile.tiktokData}
              variant="detailed"
            />
          )}

        {/* Description - shown in header for better space utilization */}
        {profile.description && (
          <Box>
            <Text
              size="sm"
              c="dimmed"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
              visibleFrom="sm"
            >
              {profile.description}
            </Text>
            <Text size="sm" c="dimmed" lineClamp={3} hiddenFrom="sm">
              {profile.description}
            </Text>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default ProfileHeader;
